import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useToast } from './use-toast';

export interface VendorCustomerCredential {
  id: string;
  workspace_id: string;
  vendor_id: string;
  customer_id: string | null;
  portal_url: string | null;
  portal_username: string | null;
  portal_password_encrypted: string | null;
  security_questions: Array<{ question: string; answer: string }>;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  customer_name?: string;
  vendor_name?: string;
}

export interface VendorCustomerCredentialInsert {
  vendor_id: string;
  customer_id?: string | null;
  portal_url?: string | null;
  portal_username?: string | null;
  portal_password_encrypted?: string | null;
  security_questions?: Array<{ question: string; answer: string }>;
  notes?: string | null;
  is_active?: boolean;
}

export const useTumVendorCustomerCredentials = (vendorId?: string) => {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch credentials for a specific vendor
  const credentialsQuery = useQuery({
    queryKey: ['vendor-customer-credentials', currentWorkspace?.id, vendorId],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      
      let query = (supabase as any)
        .from('tum_vendor_customer_credentials')
        .select(`
          *,
          customer:tum_onboarding_projects(id, project_name, customer_name)
        `)
        .eq('workspace_id', currentWorkspace.id);
      
      if (vendorId) {
        query = query.eq('vendor_id', vendorId);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return (data || []).map((item: any) => ({
        ...item,
        customer_name: item.customer?.customer_name || item.customer?.project_name || 'No Customer',
        security_questions: item.security_questions || [],
      })) as VendorCustomerCredential[];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Create credential
  const createCredentialMutation = useMutation({
    mutationFn: async (credential: VendorCustomerCredentialInsert) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await (supabase as any)
        .from('tum_vendor_customer_credentials')
        .insert({
          ...credential,
          workspace_id: currentWorkspace.id,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-customer-credentials'] });
      toast({
        title: 'Credentials Added',
        description: 'Customer credentials have been saved successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add credentials',
        variant: 'destructive',
      });
    },
  });

  // Update credential
  const updateCredentialMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<VendorCustomerCredential> & { id: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await (supabase as any)
        .from('tum_vendor_customer_credentials')
        .update({
          ...updates,
          updated_by: user?.id,
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-customer-credentials'] });
      toast({
        title: 'Credentials Updated',
        description: 'Customer credentials have been updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update credentials',
        variant: 'destructive',
      });
    },
  });

  // Delete credential
  const deleteCredentialMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('tum_vendor_customer_credentials')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-customer-credentials'] });
      toast({
        title: 'Credentials Deleted',
        description: 'Customer credentials have been removed',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete credentials',
        variant: 'destructive',
      });
    },
  });

  return {
    credentials: credentialsQuery.data || [],
    isLoading: credentialsQuery.isLoading,
    error: credentialsQuery.error,
    refetch: credentialsQuery.refetch,
    createCredential: createCredentialMutation.mutateAsync,
    updateCredential: updateCredentialMutation.mutateAsync,
    deleteCredential: deleteCredentialMutation.mutateAsync,
    isCreating: createCredentialMutation.isPending,
    isUpdating: updateCredentialMutation.isPending,
    isDeleting: deleteCredentialMutation.isPending,
  };
};
